
import { useState, useCallback } from 'react';
import {
  ArrowLeft, ArrowRight, ArrowsLeftRight, Check, CheckCircle, Database, FileCsv, Info, Lightning, MapPin, Question,
  Warning, WarningCircle, XCircle,
} from '@phosphor-icons/react';
import { supabase } from '../../../lib/supabase/client';
import { IosSchalter } from '../../../components/ui/IosSchalter';
import { AuswahlKaestchen } from '../../../components/ui/AuswahlKaestchen';
import { FormRahmen } from '../../../components/island/form/FormRahmen';
import { FormAbschnitt } from '../../../components/island/form/FormAbschnitt';
import { FormAblage } from '../../../components/island/form/FormAblage';
import { FormAuswahl } from '../../../components/island/form/FormAuswahl';
import { FormKleinFeld } from '../../../components/island/form/FormFeld';
import { FormHinweis } from '../../../components/island/form/FormHinweis';
import { FormSchritte } from '../../../components/island/form/FormSchritte';
import { FormTabelle } from '../../../components/island/form/FormTabelle';
import { FormErgebnis, FormKennzahl } from '../../../components/island/form/FormErgebnis';
import { FormKnopfleiste } from '../../../components/island/form/FormKnopfleiste';
import { useFormFehler } from '../../../components/island/form/useFormFehler';
import { useViewport } from '../../../components/island/hooks';
import { ABSCHNITT_FARBE, CHIP, FORM, INNEN, SCHALTER_AN, type ChipFarbe } from '../../../components/island/form/form-stil';

interface ColumnMapping {
  csvColumn: string;
  dbField: string;
  enabled: boolean;
  transform?: string;
}

interface ImportResult {
  total: number;
  created: number;
  matched: number;
  errors: string[];
}

const DB_FIELDS: { key: string; label: string; required?: boolean }[] = [
  { key: 'anrede', label: 'Anrede' },
  { key: 'firma_name', label: 'Firma' },
  { key: 'vorname', label: 'Vorname', required: true },
  { key: 'nachname', label: 'Nachname', required: true },
  { key: 'email', label: 'E-Mail' },
  { key: 'telefon', label: 'Telefon' },
  { key: 'mobil', label: 'Mobil' },
  { key: 'einheit', label: 'Wohnung/Einheit' },
  { key: 'geburtsdatum', label: 'Geburtsdatum' },
  { key: 'mietbeginn', label: 'Mietbeginn' },
  { key: 'mietende', label: 'Mietende' },
  { key: 'kaltmiete', label: 'Kaltmiete' },
  { key: 'nebenkosten_vorauszahlung', label: 'Nebenkosten-Vorauszahlung' },
  { key: 'kaution', label: 'Kaution' },
  { key: 'zahlungsweise', label: 'Zahlungsweise' },
  { key: 'bankverbindung', label: 'Bankverbindung' },
  { key: 'notizen', label: 'Notizen' },
  { key: '_skip', label: '-- Nicht importieren --' },
];

const AUTO_MAP: Record<string, string> = {
  'name': '_name_split',
  'vorname': 'vorname',
  'nachname': 'nachname',
  'briefanrede': 'anrede',
  'anrede': 'anrede',
  'adresse': '_adresse_split',
  'straße': '_adresse_split',
  'strasse': '_adresse_split',
  'hausnummer': '_adresse_split',
  'plz': '_plz',
  'postleitzahl': '_plz',
  'stadt': '_stadt',
  'ort': '_stadt',
  'city': '_stadt',
  'e-mail': 'email',
  'email': 'email',
  'mail': 'email',
  'telefon': '_telefon_combine',
  'telefonnummer': '_telefon_combine',
  'phone': 'telefon',
  'mobil': 'mobil',
  'handy': 'mobil',
  'mobile': 'mobil',
  'bankverbindung': 'bankverbindung',
  'iban': 'bankverbindung',
  'notizen': 'notizen',
  'bemerkung': 'notizen',
  'kontaktname': '_name_split',
  'kontaktadresse': '_adresse_split',
  'kontakttelefon': 'telefon',
  'kontakte-mail': 'email',
  'einheit': 'einheit',
  'wohnung': 'einheit',
  'wohneinheit': 'einheit',
  'apartment': 'einheit',
  'mietbeginn': 'mietbeginn',
  'mietende': 'mietende',
  'kaltmiete': 'kaltmiete',
  'miete': 'kaltmiete',
  'nebenkosten': 'nebenkosten_vorauszahlung',
  'kaution': 'kaution',
  'zahlungsweise': 'zahlungsweise',
  'geburtsdatum': 'geburtsdatum',
};

const SKIP_COLUMNS = ['id', 'benutzername', 'staat', 'land', 'landesvorwahl', 'vorwahl', 'country', 'state'];

export default function MieterCsvImportFormular({ onDone }: { onDone: () => void }) {
  const [step, setStep] = useState<1 | 2 | 3 | 4>(1);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [headers, setHeaders] = useState<string[]>([]);
  const [mappings, setMappings] = useState<ColumnMapping[]>([]);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [fileName, setFileName] = useState('');
  const [vorwahlColIndex, setVorwahlColIndex] = useState<number>(-1);
  const [excludedRows, setExcludedRows] = useState<Set<number>>(new Set());
  const [manualEdits, setManualEdits] = useState<Record<number, {vorname: string, nachname: string}>>({});
  const [matchedObjekt, setMatchedObjekt] = useState<{id: string, bezeichnung: string, strasse?: string, hausnummer?: string, stadt?: string} | null>(null);
  const fehler = useFormFehler();

  const reset = () => {
    setStep(1);
    setCsvData([]);
    setHeaders([]);
    setMappings([]);
    setImportResult(null);
    setFileName('');
    setVorwahlColIndex(-1);
    setManualEdits({});
    setMatchedObjekt(null);
  };

  const handleClose = () => {
    reset();
    onDone();
  };

  const matchFilenameToObjekt = async (filename: string) => {
    const name = filename.replace(/\.(csv|txt|tsv)$/i, '').trim();

    const numMatch = name.match(/(\d{3,})/);
    const objektNr = numMatch ? numMatch[1] : '';
    const textPart = name.replace(/^[^0-9]*\d*\s*/i, '').trim();

    if (!textPart && !objektNr) return;

    const { data: objekte } = await supabase
      .from('objekte')
      .select('id, bezeichnung, strasse, hausnummer, plz, stadt, objektnummer');

    if (!objekte || objekte.length === 0) return;

    const filenameLower = textPart.toLowerCase();

    // 1. Try matching by objektnummer
    if (objektNr) {
      const match = objekte.find(o => (o.objektnummer || '').includes(objektNr));
      if (match) { setMatchedObjekt(match); return; }
    }

    // 2. Try matching by bezeichnung in filename
    for (const obj of objekte) {
      const bez = (obj.bezeichnung || '').toLowerCase();
      if (bez && bez.length > 3 && filenameLower.includes(bez)) {
        setMatchedObjekt(obj); return;
      }
    }

    // 3. Try matching by strasse+hausnummer in filename
    for (const obj of objekte) {
      const strasse = (obj.strasse || '').toLowerCase();
      const hausnr = (obj.hausnummer || '').toLowerCase();
      if (strasse && strasse.length > 3 && filenameLower.includes(strasse)) {
        if (!hausnr || filenameLower.includes(hausnr)) {
          setMatchedObjekt(obj); return;
        }
      }
    }

    // 4. Reverse: check if filename text is contained in bezeichnung
    for (const obj of objekte) {
      const bez = (obj.bezeichnung || '').toLowerCase();
      if (bez && filenameLower.length > 5 && bez.includes(filenameLower.substring(0, 20))) {
        setMatchedObjekt(obj); return;
      }
    }
  };

  const parseCSV = (text: string): { headers: string[]; rows: string[][] } => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (lines.length < 2) return { headers: [], rows: [] };

    const parseLine = (line: string): string[] => {
      const result: string[] = [];
      let current = '';
      let inQuotes = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') {
          if (inQuotes && line[i + 1] === '"') {
            current += '"';
            i++;
          } else {
            inQuotes = !inQuotes;
          }
        } else if ((ch === ',' || ch === ';' || ch === '\t') && !inQuotes) {
          result.push(current.trim());
          current = '';
        } else {
          current += ch;
        }
      }
      result.push(current.trim());
      return result;
    };

    const hdrs = parseLine(lines[0]);
    const rows = lines.slice(1).map(parseLine).filter(r => r.some(cell => cell));
    return { headers: hdrs, rows };
  };

  const autoMapColumns = (hdrs: string[]): ColumnMapping[] => {
    return hdrs.map((h, index) => {
      const key = h.toLowerCase().replace(/[^a-zäöüß_-]/g, '').trim();

      if (SKIP_COLUMNS.includes(key)) {
        if (key === 'vorwahl') {
          setVorwahlColIndex(index);
        }
        return { csvColumn: h, dbField: '_skip', enabled: false };
      }

      const mapped = AUTO_MAP[key];
      if (mapped === '_name_split') {
        return { csvColumn: h, dbField: 'nachname', enabled: true, transform: 'name_split' };
      }
      if (mapped === '_adresse_split') {
        return { csvColumn: h, dbField: '_skip', enabled: true, transform: 'adresse_split' };
      }
      if (mapped === '_telefon_combine') {
        return { csvColumn: h, dbField: 'telefon', enabled: true, transform: 'telefon_combine' };
      }
      if (mapped === '_plz') {
        return { csvColumn: h, dbField: '_skip', enabled: true, transform: 'plz_parse' };
      }
      if (mapped === '_stadt') {
        return { csvColumn: h, dbField: '_skip', enabled: true, transform: 'stadt_parse' };
      }
      if (mapped) {
        return { csvColumn: h, dbField: mapped, enabled: true };
      }

      return { csvColumn: h, dbField: '_skip', enabled: false };
    });
  };

  const handleFile = useCallback((file: File) => {
    fehler.leeren();
    setFileName(file.name);
    matchFilenameToObjekt(file.name);
    const tryParse = (text: string): boolean => {
      const { headers: hdrs, rows } = parseCSV(text);
      if (hdrs.length === 0) return false;
      setHeaders(hdrs);
      setCsvData(rows);
      setMappings(autoMapColumns(hdrs));
      setStep(2);
      return true;
    };

    // Try UTF-8 first, then Windows-1252 if garbled characters detected
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      // Check for garbled characters (replacement character or typical UTF-8 misread patterns)
      if (text.includes('\ufffd') || /Ã¶|Ã¤|Ã¼|Ã–|Ã„|Ãœ|Ã/.test(text)) {
        // Re-read with Windows-1252
        const reader2 = new FileReader();
        reader2.onload = (e2) => {
          const text2 = e2.target?.result as string;
          if (!tryParse(text2)) {
            fehler.zeige('CSV-Datei konnte nicht gelesen werden.');
          }
        };
        reader2.readAsText(file, 'windows-1252');
      } else {
        if (!tryParse(text)) {
          fehler.zeige('CSV-Datei konnte nicht gelesen werden.');
        }
      }
    };
    reader.readAsText(file, 'UTF-8');
  }, [fehler]);

  // Abgelegt: nur die erste Datei und nur .csv/.txt/.tsv (wie bisher). Ausgewählt: die erste Datei.
  const dateienErhalten = (files: File[], quelle: 'auswahl' | 'ablage') => {
    const file = files[0];
    if (quelle === 'ablage') {
      if (file && (file.name.endsWith('.csv') || file.name.endsWith('.txt') || file.name.endsWith('.tsv'))) {
        handleFile(file);
      }
      return;
    }
    if (file) handleFile(file);
  };

  const toggleMapping = (index: number) => {
    setMappings(prev => prev.map((m, i) => i === index ? { ...m, enabled: !m.enabled } : m));
  };

  const updateMappingField = (index: number, dbField: string) => {
    setMappings(prev => prev.map((m, i) => {
      if (i !== index) return m;
      return {
        ...m,
        dbField,
        enabled: dbField !== '_skip',
        transform: undefined
      };
    }));
  };

  const splitName = (fullName: string): { vorname: string; nachname: string; firma?: string; _needsReview?: boolean; _originalName?: string }[] => {
    if (!fullName) return [{ vorname: '', nachname: '' }];

    const cleaned = fullName.replace(/,\s*$/, '').trim();

    // Check for company indicators
    const companyIndicators = ['GmbH', 'GbR', 'KG', 'OHG', 'AG', 'e.K.', 'Co KG', 'UG', 'e.V.', 'e. V.', 'OW-RE', 'Immobilien', 'Versicherung', 'Gemeinde', 'Kirche', 'Stiftung', 'Verein', 'Genossenschaft', 'Stadtwerke', 'Sparkasse', 'Volksbank', 'Diakonie', 'Caritas', 'GmbH+', 'Co.KG', 'mbH', 'Inc', 'Ltd', 'LLC'];
    const companyIndicatorsLower = companyIndicators.map(c => c.toLowerCase());
    const cleanedLower = cleaned.toLowerCase();
    if (companyIndicatorsLower.some(ind => cleanedLower.includes(ind))) {
      return [{ vorname: '', nachname: '', firma: cleaned }];
    }

    // Heuristic: if name has 4+ words and no typical name patterns, likely a company/org
    const words = cleaned.split(/\s+/);
    if (words.length >= 4) {
      const hasTypicalNamePattern = /^(Herr|Frau|Dr\.|Prof\.)/i.test(cleaned) ||
        (words.length <= 5 && /^[A-ZÄÖÜ][a-zäöüß]+$/.test(words[0]) && /^[A-ZÄÖÜ][a-zäöüß]+$/.test(words[words.length - 1]));
      if (!hasTypicalNamePattern && !cleaned.includes(' und ') && !cleaned.includes(' & ')) {
        return [{ vorname: '', nachname: '', firma: cleaned }];
      }
    }

    // Handle "Frau ... Herr ..." or "Herr ... Frau ..." pattern (couples with titles)
    const herrFrauPattern = /\b(?:Herr|Frau)\b/gi;
    const titleMatches = [...cleaned.matchAll(herrFrauPattern)];
    if (titleMatches.length >= 2) {
      const results: { vorname: string; nachname: string }[] = [];
      for (let i = 0; i < titleMatches.length; i++) {
        const start = titleMatches[i].index! + titleMatches[i][0].length;
        const end = i + 1 < titleMatches.length ? titleMatches[i + 1].index! : cleaned.length;
        const namePart = cleaned.substring(start, end).trim();
        const parts = namePart.split(/\s+/).filter(Boolean);
        if (parts.length >= 2) {
          results.push({ vorname: parts.slice(0, -1).join(' '), nachname: parts[parts.length - 1] });
        } else if (parts.length === 1) {
          results.push({ vorname: parts[0], nachname: '' });
        }
      }
      // If the last person has no nachname, try to get it from the previous person's pattern or leave empty
      return results;
    }

    // Handle comma-separated name: "Schwede, Bjoern" -> likely Nachname, Vorname format
    const hasCommaSep = /,\s*[A-ZÄÖÜ]/.test(cleaned);
    const hasUnd = cleaned.includes(' und ') || cleaned.includes(' & ');

    if (hasCommaSep && !hasUnd) {
      const commaParts = cleaned.split(/,\s*/);
      if (commaParts.length === 2) {
        const part1 = commaParts[0].trim();
        const part2 = commaParts[1].trim();
        const part1Words = part1.split(/\s+/);
        const part2Words = part2.split(/\s+/);

        if (part1Words.length >= 2 && part2Words.length >= 2) {
          // Both parts have full names -> two separate persons: "Sven Achenbach, Laura Niggemann"
          return [
            { vorname: part1Words.slice(0, -1).join(' '), nachname: part1Words[part1Words.length - 1] },
            { vorname: part2Words.slice(0, -1).join(' '), nachname: part2Words[part2Words.length - 1] },
          ];
        }

        // Single-word parts -> likely "Nachname, Vorname" -> review
        return [{ vorname: part2, nachname: part1, _needsReview: true, _originalName: cleaned }];
      }
    }

    // Handle "und" / "&" -- split into separate persons
    if (hasUnd) {
      const allPersons: string[] = [];
      const commaParts = cleaned.split(/,\s*/);

      for (const part of commaParts) {
        const undParts = part.split(/\s+(?:und|&)\s+/);
        allPersons.push(...undParts.map(p => p.trim()).filter(Boolean));
      }

      // Detect shared lastname: if first person has 2+ words and last person has 2+ words
      const results: { vorname: string; nachname: string }[] = [];
      const lastPerson = allPersons[allPersons.length - 1].split(/\s+/);
      const sharedLastname = lastPerson.length >= 2 ? lastPerson[lastPerson.length - 1] : '';

      for (let i = 0; i < allPersons.length; i++) {
        const personParts = allPersons[i].split(/\s+/);

        if (personParts.length >= 2) {
          // Full name: "Vorname Nachname"
          results.push({
            vorname: personParts.slice(0, -1).join(' '),
            nachname: personParts[personParts.length - 1]
          });
        } else if (personParts.length === 1 && sharedLastname) {
          // Only first name, use shared lastname from last person
          results.push({
            vorname: personParts[0],
            nachname: sharedLastname
          });
        } else {
          results.push({ vorname: '', nachname: personParts[0] || '' });
        }
      }

      return results;
    }

    // Single person
    // Remove titles (including Herr/Frau)
    let name = cleaned.replace(/^(Herr\s+|Frau\s+|Dr\.\s*|Prof\.\s*|Dipl\.\s*-?\w+\s*)/i, '').trim();

    // Handle "geb."
    name = name.replace(/\s*geb\.\s*\w+/i, '').trim();

    const parts = name.split(/\s+/);
    if (parts.length === 1) return [{ vorname: '', nachname: parts[0] }];
    if (parts.length === 2) return [{ vorname: parts[0], nachname: parts[1] }];

    // Last part is nachname, rest is vorname
    return [{
      vorname: parts.slice(0, -1).join(' '),
      nachname: parts[parts.length - 1]
    }];
  };

  const splitAdresse = (adresse: string): { strasse: string; hausnummer: string } => {
    if (!adresse) return { strasse: '', hausnummer: '' };
    // Match street name + house number (number possibly with letter suffix)
    const match = adresse.match(/^(.+?)\s+(\d+\s*[a-zA-Z]?(?:\s*[-–]\s*\d+\s*[a-zA-Z]?)?)$/);
    if (match) {
      return { strasse: match[1].trim(), hausnummer: match[2].trim() };
    }
    return { strasse: adresse, hausnummer: '' };
  };

  const buildRows = (): Record<string, string>[] => {
    const allRows: Record<string, string>[] = [];

    csvData.forEach(row => {
      const baseRecord: Record<string, string> = {};
      let persons: { vorname: string; nachname: string; firma?: string; _needsReview?: boolean; _originalName?: string }[] = [];

      mappings.forEach((m, colIdx) => {
        if (!m.enabled || m.dbField === '_skip') {
          // Still process address-related transforms even when dbField is _skip
          if (m.enabled && m.transform === 'adresse_split') {
            const value = row[colIdx] || '';
            const plzStadtMatch = value.match(/,?\s*(\d{5})\s+(.+)$/);
            let addrPart = value;
            if (plzStadtMatch) {
              baseRecord['_addr_plz'] = plzStadtMatch[1];
              baseRecord['_addr_stadt'] = plzStadtMatch[2].trim();
              addrPart = value.substring(0, value.indexOf(plzStadtMatch[0])).replace(/,\s*$/, '').trim();
            }
            const { strasse, hausnummer } = splitAdresse(addrPart);
            baseRecord['_addr_strasse'] = strasse;
            baseRecord['_addr_hausnummer'] = hausnummer;
            return;
          }
          if (m.enabled && m.transform === 'plz_parse') {
            baseRecord['_addr_plz'] = row[colIdx] || '';
            return;
          }
          if (m.enabled && m.transform === 'stadt_parse') {
            baseRecord['_addr_stadt'] = row[colIdx] || '';
            return;
          }
          return;
        }
        const value = row[colIdx] || '';

        if (m.transform === 'name_split') {
          persons = splitName(value);
        } else if (m.transform === 'adresse_split') {
          // Parse full address for matching purposes, store in _addr_ prefixed fields
          const plzStadtMatch = value.match(/,?\s*(\d{5})\s+(.+)$/);
          let addrPart = value;
          if (plzStadtMatch) {
            baseRecord['_addr_plz'] = plzStadtMatch[1];
            baseRecord['_addr_stadt'] = plzStadtMatch[2].trim();
            addrPart = value.substring(0, value.indexOf(plzStadtMatch[0])).replace(/,\s*$/, '').trim();
          }
          const { strasse, hausnummer } = splitAdresse(addrPart);
          baseRecord['_addr_strasse'] = strasse;
          baseRecord['_addr_hausnummer'] = hausnummer;
        } else if (m.transform === 'telefon_combine') {
          const vorwahl = vorwahlColIndex >= 0 ? (row[vorwahlColIndex] || '') : '';
          const nummer = value;
          if (vorwahl && nummer) {
            baseRecord['telefon'] = `0${vorwahl} ${nummer}`;
          } else if (nummer) {
            baseRecord['telefon'] = nummer;
          }
        } else if (m.transform === 'plz_parse') {
          baseRecord['_addr_plz'] = value;
        } else if (m.transform === 'stadt_parse') {
          baseRecord['_addr_stadt'] = value;
        } else {
          baseRecord[m.dbField] = value;
        }
      });

      // Create one row per person
      if (persons.length === 0) {
        persons = [{ vorname: '', nachname: '' }];
      }

      // If splitName returned multiple persons, use shared nachname from baseRecord as fallback
      const sharedNachname = baseRecord['nachname'] || '';

      for (const person of persons) {
        const record = { ...baseRecord };
        record['vorname'] = person.vorname;
        // Use person's nachname if available, otherwise fall back to shared nachname from separate column
        record['nachname'] = person.nachname || sharedNachname;
        if (person.firma) record['firma_name'] = person.firma;
        if (person._needsReview) record['_needs_review'] = 'true';
        if (person._originalName) record['_original_name'] = person._originalName;

        // Clean trailing commas from nachname
        if (record['nachname']) {
          record['nachname'] = record['nachname'].replace(/,\s*$/, '').trim();
        }

        if (record['nachname'] || record['firma_name']) {
          allRows.push(record);
        }
      }
    });

    return allRows;
  };

  const handleImport = async () => {
    setImporting(true);
    const rows = importableRows;
    const result: ImportResult = { total: rows.length, created: 0, matched: 0, errors: [] };

    try {
      // Load all objects for address matching
      const { data: objekte } = await supabase
        .from('objekte')
        .select('id, bezeichnung, strasse, hausnummer, plz, stadt');

      for (const row of rows) {
        try {
          // Check for duplicate by name or firma
          const displayName = row.firma_name || `${row.vorname} ${row.nachname}`.trim();
          let existing: any[] | null = null;
          if (row.firma_name && !row.nachname) {
            const { data } = await supabase.from('mieter').select('id, objekt_id').eq('firma_name', row.firma_name);
            existing = data;
          } else {
            const query = supabase.from('mieter').select('id, objekt_id').eq('nachname', row.nachname);
            if (row.vorname) query.eq('vorname', row.vorname);
            const { data } = await query;
            existing = data;
          }

          if (existing && existing.length > 0) {
            // Mieter exists -- but check if Objekt assignment is missing
            if (matchedObjekt) {
              const existingMieter = existing[0];
              if (!existingMieter.objekt_id) {
                await supabase
                  .from('mieter')
                  .update({ objekt_id: matchedObjekt.id })
                  .eq('id', existingMieter.id);
                result.matched++;
              }
            }
            result.errors.push(`${displayName} existiert bereits`);
            continue;
          }

          // Insert mieter
          // zahlungsweise has CHECK constraint: only 'überweisung','lastschrift','bar','dauerauftrag' or null
          const validZahlungsweisen = ['überweisung', 'lastschrift', 'bar', 'dauerauftrag'];
          const zahlungsweise = row.zahlungsweise ? row.zahlungsweise.toLowerCase().trim() : null;

          const insertData: Record<string, any> = {
            vorname: row.vorname || '',
            nachname: row.nachname || '',
            firma_name: row.firma_name || '',
            anrede: row.anrede || '',
            email: row.email || '',
            telefon: row.telefon || '',
            mobil: row.mobil || '',
            einheit: row.einheit || '',
            geburtsdatum: row.geburtsdatum || null,
            mietbeginn: row.mietbeginn || '',
            mietende: row.mietende || null,
            kaltmiete: row.kaltmiete ? parseFloat(row.kaltmiete.replace(',', '.')) || null : null,
            nebenkosten_vorauszahlung: row.nebenkosten_vorauszahlung ? parseFloat(row.nebenkosten_vorauszahlung.replace(',', '.')) || null : null,
            kaution: row.kaution ? parseFloat(row.kaution.replace(',', '.')) || null : null,
            zahlungsweise: zahlungsweise && validZahlungsweisen.includes(zahlungsweise) ? zahlungsweise : null,
            bankverbindung: row.bankverbindung || '',
            notizen: row.notizen || '',
            status: matchedObjekt ? 'aktiv' : 'inaktiv',
          };

          // Clean empty string dates to null (but mietbeginn stays as empty string since NOT NULL)
          if (insertData.geburtsdatum === '') insertData.geburtsdatum = null;
          if (insertData.mietende === '') insertData.mietende = null;

          // If filename matched an Objekt, assign it
          if (matchedObjekt) {
            insertData.objekt_id = matchedObjekt.id;
          }

          const { data: inserted, error: insertError } = await supabase
            .from('mieter')
            .insert([insertData])
            .select('id')
            .single();

          if (insertError) {
            result.errors.push(`${row.vorname} ${row.nachname}: ${insertError.message}`);
            continue;
          }

          result.created++;

          if (matchedObjekt && inserted) {
            result.matched++;
          }
          // Otherwise try to match to object by address
          else if (objekte && row._addr_strasse && inserted) {
            const streetNorm = row._addr_strasse.toLowerCase().replace(/straße|strasse|str\./g, 'str').replace(/\s+/g, '');
            const hausnrNorm = (row._addr_hausnummer || '').replace(/\s/g, '').toLowerCase();

            for (const obj of objekte) {
              const objStreetNorm = (obj.strasse || '').toLowerCase().replace(/straße|strasse|str\./g, 'str').replace(/\s+/g, '');
              const objHausnrNorm = (obj.hausnummer || '').replace(/\s/g, '').toLowerCase();

              if (streetNorm === objStreetNorm && hausnrNorm && objHausnrNorm) {
                // Check hausnummer: exact match or contained
                if (hausnrNorm === objHausnrNorm || objHausnrNorm.includes(hausnrNorm) || hausnrNorm.includes(objHausnrNorm)) {
                  // Also check city if available
                  const cityMatch = !row._addr_stadt || !obj.stadt ||
                    row._addr_stadt.toLowerCase().includes(obj.stadt.toLowerCase()) ||
                    obj.stadt.toLowerCase().includes(row._addr_stadt.toLowerCase());

                  if (cityMatch) {
                    await supabase
                      .from('mieter')
                      .update({ objekt_id: obj.id, status: 'aktiv' })
                      .eq('id', inserted.id);
                    result.matched++;
                    break;
                  }
                }
              }
            }
          }
        } catch (rowErr: any) {
          result.errors.push(`${row.vorname} ${row.nachname}: ${rowErr.message}`);
        }
      }
    } catch (err: any) {
      result.errors.push(`Allgemeiner Fehler: ${err.message}`);
    }

    setImportResult(result);
    setImporting(false);
    setStep(4);
    // onSuccess() call removed — list reloads via onDone() when user closes
  };

  const INVALID_TERMS = [
    'eigentümergemeinschaft', 'eigentümerschaft', 'hausverwaltung',
    'verwaltung', 'gemeinschaft', 'erbengemeinschaft', 'unbekannt',
    'mieter', 'vermieter', 'bewohner', 'inhaber', 'weg ',
    'eigentümer'
  ];

  const validateRow = (row: Record<string, string>): string | null => {
    const vorname = (row.vorname || '').trim();
    const nachname = (row.nachname || '').trim();
    const firma = (row.firma_name || '').trim();

    // Firma is valid on its own — no name needed
    if (firma) return null;

    // No name at all
    if (!vorname && !nachname) return 'Kein Name vorhanden';

    // Generic terms instead of real names
    if (nachname && INVALID_TERMS.some(t => nachname.toLowerCase().includes(t))) {
      return `"${nachname}" ist kein gültiger Name`;
    }
    if (vorname && INVALID_TERMS.some(t => vorname.toLowerCase().includes(t))) {
      return `"${vorname}" ist kein gültiger Name`;
    }

    // No vorname and nachname is suspiciously short or generic
    if (!vorname && nachname.length <= 2) {
      return `Nachname "${nachname}" zu kurz`;
    }

    return null;
  };

  const previewRows = buildRows();

  // Detect duplicates by vorname+nachname -- keep the most complete entry
  const dataScore = (row: Record<string, string>): number => {
    let score = 0;
    if (row.email) score += 2;
    if (row.telefon) score += 2;
    if (row.mobil) score += 1;
    if (row.einheit) score += 2;
    if (row.kaltmiete) score += 1;
    if (row.mietbeginn) score += 1;
    if (row.bankverbindung) score += 1;
    return score;
  };

  const duplicateGroups = new Map<string, number[]>();
  previewRows.forEach((row, i) => {
    const key = `${(row.vorname || '').trim().toLowerCase()}|${(row.nachname || '').trim().toLowerCase()}|${(row.firma_name || '').trim().toLowerCase()}`;
    if (key === '||') return;
    if (!duplicateGroups.has(key)) {
      duplicateGroups.set(key, []);
    }
    duplicateGroups.get(key)!.push(i);
  });

  const duplicateIndices = new Set<number>();
  const bestEntryIndex = new Map<string, number>();
  duplicateGroups.forEach((indices, key) => {
    if (indices.length <= 1) return;
    // Find the entry with the highest data score
    let bestIdx = indices[0];
    let bestScore = dataScore(previewRows[bestIdx]);
    for (let j = 1; j < indices.length; j++) {
      const score = dataScore(previewRows[indices[j]]);
      if (score > bestScore) {
        bestScore = score;
        bestIdx = indices[j];
      }
    }
    bestEntryIndex.set(key, bestIdx);
    // Mark all others as duplicates
    for (const idx of indices) {
      if (idx !== bestIdx) duplicateIndices.add(idx);
    }
  });

  const validatedRows = previewRows.map((row, i) => {
    const error = validateRow(row);
    if (error) return { row, index: i, error, needsReview: false };
    if (duplicateIndices.has(i)) {
      const key = `${(row.vorname || '').trim().toLowerCase()}|${(row.nachname || '').trim().toLowerCase()}|${(row.firma_name || '').trim().toLowerCase()}`;
      const bestIdx = bestEntryIndex.get(key)!;
      return { row, index: i, error: `Duplikat (vollständigerer Eintrag in Zeile ${bestIdx + 1})`, needsReview: false };
    }
    return { row, index: i, error: null, needsReview: row._needs_review === 'true' };
  });
  const problemRows = validatedRows.filter(r => r.error);
  const reviewRows = validatedRows.filter(r => !r.error && r.needsReview);
  const validRows = validatedRows.filter(r => !r.error && !r.needsReview);

  // Initialize excludedRows with problem indices when entering step 3
  const initExclusions = () => {
    setExcludedRows(new Set(problemRows.map(r => r.index)));
  };

  const toggleRowExclusion = (index: number) => {
    setExcludedRows(prev => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const importableRows = previewRows
    .map((row, i) => {
      if (excludedRows.has(i)) return null;
      if (manualEdits[i]) {
        return { ...row, vorname: manualEdits[i].vorname, nachname: manualEdits[i].nachname };
      }
      return row;
    })
    .filter(Boolean) as Record<string, string>[];

  const { width } = useViewport();
  const schmal = width < 600;
  const leer = (v: string | undefined) => v || '–';
  const zelle = (v: string | undefined, farbe?: string, fett?: boolean) => (
    <span style={{ color: farbe ?? 'rgba(255,255,255,0.45)', fontWeight: fett ? 800 : 600 }}>{leer(v)}</span>
  );
  const chip = (text: string, farbe: ChipFarbe) => (
    <span style={{ fontSize: 11, fontWeight: 800, padding: '4px 9px', borderRadius: 999, color: CHIP[farbe].text, background: CHIP[farbe].flaeche, whiteSpace: 'nowrap' }}>{text}</span>
  );
  const kaestchen = (index: number) => (
    <AuswahlKaestchen an={!excludedRows.has(index)} onUmschalten={() => toggleRowExclusion(index)} label={`Zeile ${index + 1} importieren`} />
  );
  const text = { margin: 0, fontSize: 12.5, fontWeight: 600, lineHeight: 1.5, color: 'rgba(255,255,255,0.55)' } as const;

  const knopfleiste = step === 1 ? (
    <FormKnopfleiste abbrechen={{ label: 'Abbrechen', onClick: handleClose }} />
  ) : step === 2 ? (
    <FormKnopfleiste abbrechen={{ label: 'Zurück', icon: ArrowLeft, onClick: () => setStep(1) }}
      haupt={{
        label: 'Vorschau', icon: ArrowRight, iconRechts: true, typ: 'button', onClick: () => { initExclusions(); setStep(3); },
        gesperrt: !mappings.some(m => m.enabled && (m.dbField === 'nachname' || m.transform === 'name_split')),
      }} />
  ) : step === 3 ? (
    <FormKnopfleiste abbrechen={{ label: 'Zurück', icon: ArrowLeft, onClick: () => setStep(2) }}
      haupt={{
        label: `${importableRows.length} Einträge importieren`, icon: Database, typ: 'button', onClick: handleImport,
        laedt: importing, ladeLabel: 'Importiere...', gesperrt: !importing && importableRows.length === 0,
      }} />
  ) : (
    <FormKnopfleiste haupt={{ label: 'Schließen', icon: Check, typ: 'button', onClick: handleClose }} />
  );

  return (
    <FormRahmen fehler={fehler.text} knopfleiste={knopfleiste}>
      <FormSchritte schritte={['Upload', 'Mapping', 'Vorschau', 'Ergebnis']} aktiv={step} />

      {/* STEP 1: Upload */}
      {step === 1 && (
        <FormAblage icon={FileCsv} grossesIcon verlauf={ABSCHNITT_FARBE.gruen.verlauf} accept=".csv,.txt,.tsv"
          ariaLabel="CSV-Datei auswählen" tip="CSV-Datei auswählen"
          titel="CSV-Datei hochladen" titelBeimZiehen="Datei loslassen"
          text="Drag & Drop oder klicken zum Auswählen"
          hinweis="Unterstützt: .csv, .txt, .tsv (Komma, Semikolon oder Tab getrennt)"
          onDateien={dateienErhalten} />
      )}

      {/* STEP 2: Mapping */}
      {step === 2 && (
        <>
          <FormAbschnitt titel="Auto-Mapping aktiv" icon={Lightning} farbe="blau"
            tip="Auto-Mapping – Jede Spalte der Datei wird einem Feld zugeordnet. Abgeschaltete Spalten werden ignoriert."
            rechts={chip(`${mappings.filter(m => m.enabled).length} von ${mappings.length} gemappt`, 'gruen')}>
            <p style={text}>Spalten wurden automatisch zugeordnet. Du kannst die Zuordnung anpassen oder Spalten deaktivieren.</p>
            <div className="grid" style={{
              gridTemplateColumns: 'minmax(0,1fr) 18px minmax(0,1fr) 46px', gap: 10, padding: '0 12px',
              fontSize: 10.5, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.4)',
            }}>
              <span>CSV-Spalte</span><span /><span>DB-Feld</span><span>Aktiv</span>
            </div>
            {mappings.map((m, i) => (
              <div key={i} className="grid items-start" style={{
                gridTemplateColumns: 'minmax(0,1fr) 18px minmax(0,1fr) 46px', gap: 10, padding: '8px 10px 8px 12px', borderRadius: 15,
                background: INNEN.flaeche, boxShadow: INNEN.ring, opacity: m.enabled ? 1 : 0.5, transition: 'opacity .25s',
              }}>
                <span className="flex min-w-0 items-center" style={{ gap: 6, minHeight: 28 }}>
                  <FileCsv aria-hidden size={13} weight="fill" color={m.enabled ? FORM.gold : 'rgba(255,255,255,0.3)'} className="shrink-0" />
                  <span className="truncate" style={{ fontFamily: 'ui-monospace,Menlo,monospace', fontSize: 12.5, fontWeight: 600 }}>{m.csvColumn}</span>
                  {m.transform && (
                    <span style={{ fontSize: 9.5, fontWeight: 800, padding: '2px 6px', borderRadius: 6, textTransform: 'uppercase', color: CHIP.gold.text, background: CHIP.gold.flaeche }}>
                      {m.transform === 'name_split' ? 'Split' :
                       m.transform === 'adresse_split' ? 'Split' :
                       m.transform === 'telefon_combine' ? 'Kombi' :
                       m.transform === 'plz_parse' ? 'Adresse' :
                       m.transform === 'stadt_parse' ? 'Adresse' : ''}
                    </span>
                  )}
                </span>
                <ArrowRight aria-hidden size={13} weight="bold" color={m.enabled ? FORM.gold : 'rgba(255,255,255,0.35)'} style={{ marginTop: 8 }} />
                <FormAuswahl kompakt label={`DB-Feld für ${m.csvColumn}`} wert={m.dbField} onChange={(v) => updateMappingField(i, v)}
                  optionen={DB_FIELDS.map(f => ({ wert: f.key, label: f.required ? `${f.label} *` : f.label }))} />
                <IosSchalter an={m.enabled} onChange={() => toggleMapping(i)} label={`Aktiv: ${m.csvColumn}`} anFarbe={SCHALTER_AN} />
              </div>
            ))}
          </FormAbschnitt>

          {mappings.some(m => m.transform === 'name_split') && (
            <FormHinweis icon={Info} farbe="blau">
              <strong>Name-Split:</strong> Die Spalte "Name" wird automatisch in Vor- und Nachname aufgeteilt.
            </FormHinweis>
          )}
          {mappings.some(m => m.transform === 'adresse_split') && (
            <FormHinweis icon={Info} farbe="blau">
              <strong>Adresse-Split:</strong> Die Spalte "Adresse" wird für die automatische Objekt-Zuordnung verwendet.
            </FormHinweis>
          )}
          {matchedObjekt && (
            <FormHinweis icon={MapPin} farbe="gruen">
              <strong>Objekt erkannt:</strong> Alle importierten Mieter werden automatisch zugeordnet zu:
              <span className="block" style={{ marginTop: 4, fontWeight: 800, color: CHIP.gruen.text }}>
                {matchedObjekt.bezeichnung} {matchedObjekt.strasse ? `— ${matchedObjekt.strasse} ${matchedObjekt.hausnummer || ''}, ${matchedObjekt.stadt || ''}` : ''}
              </span>
            </FormHinweis>
          )}
        </>
      )}

      {/* STEP 3: Preview */}
      {step === 3 && (
        <>
          {/* Problem rows */}
          {problemRows.length > 0 && (
            <FormAbschnitt titel="Probleme erkannt" icon={Warning} farbe="orange"
              tip="Probleme erkannt – Einträge mit Problemen werden standardmäßig nicht importiert"
              rechts={chip(`${problemRows.length} Einträge`, 'orange')}>
              <p style={text}>
                Diese Einträge haben ungültige oder fehlende Daten. Sie werden standardmäßig <strong style={{ color: 'rgba(255,255,255,0.8)' }}>nicht importiert</strong>. Du kannst einzelne Einträge per Checkbox trotzdem einschließen.
              </p>
              <FormTabelle beschriftung="Probleme erkannt" farbe="orange"
                spalten={['', 'Vorname', 'Nachname', 'Firma', 'Einheit', 'E-Mail', 'Problem']}
                zeilen={problemRows.map(({ row, index, error }) => ({
                  key: index, gedimmt: excludedRows.has(index),
                  zellen: [kaestchen(index), zelle(row.vorname, 'rgba(255,255,255,0.65)'), zelle(row.nachname, FORM.text, true),
                    zelle(row.firma_name, CHIP.violett.text), zelle(row.einheit, FORM.gold), zelle(row.email), chip(error ?? '', 'orange')],
                }))} />
            </FormAbschnitt>
          )}

          {/* Review rows - manual assignment */}
          {reviewRows.length > 0 && (
            <FormAbschnitt titel="Manuelle Zuordnung" icon={Question} farbe="blau" rechts={chip(`${reviewRows.length} Einträge`, 'blau')}>
              <p style={text}>
                Bei diesen Einträgen konnte Vor- und Nachname nicht eindeutig zugeordnet werden. Prüfe die Vorschläge und passe sie bei Bedarf an. Mit <ArrowsLeftRight aria-label="Tauschen" size={12} weight="bold" color={CHIP.blau.text} style={{ display: 'inline', verticalAlign: '-2px' }} /> kannst du Vor- und Nachname tauschen.
              </p>
              <FormTabelle beschriftung="Manuelle Zuordnung" farbe="blau"
                spalten={['', 'Vorname', '', 'Nachname', 'Firma', 'Einheit', 'E-Mail', 'Original']}
                zeilen={reviewRows.map(({ row, index }) => {
                  const edited = manualEdits[index] || { vorname: row.vorname, nachname: row.nachname };
                  return {
                    key: index, gedimmt: excludedRows.has(index),
                    zellen: [
                      kaestchen(index),
                      <FormKleinFeld key="v" value={edited.vorname} breite={schmal ? 110 : 140} ariaLabel={`Vorname Zeile ${index + 1}`}
                        onChange={(e) => setManualEdits(prev => ({ ...prev, [index]: { ...edited, vorname: e.target.value } }))} />,
                      <button key="t" type="button" aria-label="Vor- und Nachname tauschen" data-tip="Vor- und Nachname tauschen"
                        onClick={() => setManualEdits(prev => ({ ...prev, [index]: { vorname: edited.nachname, nachname: edited.vorname } }))}
                        className="flex cursor-pointer items-center justify-center rounded-full border-none"
                        style={{ width: 30, height: 30, color: CHIP.blau.text, background: CHIP.blau.flaeche }}>
                        <ArrowsLeftRight size={14} weight="bold" />
                      </button>,
                      <FormKleinFeld key="n" value={edited.nachname} breite={schmal ? 110 : 140} ariaLabel={`Nachname Zeile ${index + 1}`}
                        onChange={(e) => setManualEdits(prev => ({ ...prev, [index]: { ...edited, nachname: e.target.value } }))} />,
                      zelle(row.firma_name, CHIP.violett.text), zelle(row.einheit), zelle(row.email),
                      <span key="o" style={{ fontStyle: 'italic', color: 'rgba(255,255,255,0.35)' }}>{leer(row._original_name)}</span>,
                    ],
                  };
                })} />
            </FormAbschnitt>
          )}

          {/* Valid rows */}
          <FormAbschnitt titel="Gültige Einträge" icon={CheckCircle} farbe="gruen" tip="Gültige Einträge – Diese Zeilen werden angelegt"
            rechts={chip(`${validRows.length} Einträge`, 'gruen')}>
            <FormTabelle beschriftung="Gültige Einträge"
              spalten={['', 'Vorname', 'Nachname', 'Firma', 'Einheit', 'Mietbeginn', 'Kaltmiete', 'E-Mail', 'Telefon']}
              zeilen={validRows.map(({ row, index }) => ({
                key: index, gedimmt: excludedRows.has(index),
                zellen: [kaestchen(index), zelle(row.vorname, 'rgba(255,255,255,0.65)'), zelle(row.nachname, FORM.text, true),
                  zelle(row.firma_name, CHIP.violett.text), zelle(row.einheit, FORM.gold), zelle(row.mietbeginn), zelle(row.kaltmiete),
                  zelle(row.email), zelle(row.telefon)],
              }))} />
          </FormAbschnitt>

          <FormHinweis icon={MapPin} farbe="gold">
            <strong style={{ color: FORM.text }}>Auto-Zuordnung:</strong> Nach dem Import werden Mieter automatisch passenden Objekten zugeordnet (über Adressabgleich oder Dateinamen-Erkennung).
          </FormHinweis>
        </>
      )}

      {/* STEP 4: Result */}
      {step === 4 && importResult && (
        <>
          <FormErgebnis titel="Import abgeschlossen"
            icon={importResult.errors.length === 0 ? CheckCircle : WarningCircle}
            verlauf={importResult.errors.length === 0 ? ABSCHNITT_FARBE.gruen.verlauf : ABSCHNITT_FARBE.orange.verlauf}
            toenung={importResult.errors.length === 0 ? 'rgba(95,211,138,0.18)' : 'rgba(255,179,64,0.14)'}>
            <div className="grid w-full" style={{ gridTemplateColumns: 'repeat(3,minmax(0,1fr))', gap: 10 }}>
              <FormKennzahl wert={importResult.created} label="Erstellt" />
              <FormKennzahl wert={importResult.matched} label="Objekt-Zuordnung" farbe={FORM.gold} />
              <FormKennzahl wert={importResult.errors.length} label="Übersprungen" farbe={FORM.rot} />
            </div>
          </FormErgebnis>
          {importResult.errors.length > 0 && (
            <FormAbschnitt titel="Details">
              <div className="pf-scroll flex flex-col overflow-y-auto" style={{ gap: 6, maxHeight: 200 }}>
                {importResult.errors.map((err, i) => (
                  <div key={i} className="flex items-start" style={{ gap: 8, fontSize: 12, fontWeight: 600, lineHeight: 1.45, color: 'rgba(255,255,255,0.6)' }}>
                    <XCircle aria-hidden size={13} weight="fill" color={CHIP.orange.text} className="shrink-0" style={{ marginTop: 2 }} />
                    <span>{err}</span>
                  </div>
                ))}
              </div>
            </FormAbschnitt>
          )}
        </>
      )}
    </FormRahmen>
  );
}
